import * as React from 'react';
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { withTranslation } from 'react-i18next';
import axios from 'axios';
import { useUserStore } from '../../store';
import { resolveDocumentLanguage } from '../../utils/languages';
import { buildDefaultInvoiceNotes } from '../../utils/invoiceSplit';
import { formatPriceWithCurrency } from '../../utils/vat';

/**
 * Confirm step for the quick cash / card payment buttons on the work order.
 *
 * Confirming creates the invoice exactly as CreateInvoiceDialog would with its defaults
 * (recipient's language, description + invoice-flagged customer notes) and hands it to
 * onInvoiceCreated, which navigates to the invoice where the payment itself is taken.
 */
function QuickPayConfirmDialog({ t, open, method, maintenance, total, handleClose, onInvoiceCreated, onBillingSettingsNeeded }) {
  const currentOrganization = useUserStore((state) => state.currentOrganization);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const payer = maintenance?.billing_customer || maintenance?.customer;

  const close = () => {
    if (creating) return;
    setError(null);
    handleClose();
  };

  const handleConfirm = async () => {
    if (creating) return;
    try {
      setCreating(true);
      setError(null);
      const response = await axios.post('/api/invoices/', {
        maintenance_id: maintenance.id,
        language: resolveDocumentLanguage(payer, currentOrganization),
        notes: buildDefaultInvoiceNotes(maintenance),
      });
      onInvoiceCreated(response.data, method);
    } catch (err) {
      const errorMessage = err.response?.data?.error || '';
      if (errorMessage.includes('IBAN is required')) {
        // The normal create dialog owns the "fill in bank details" flow.
        handleClose();
        onBillingSettingsNeeded();
        return;
      }
      if (errorMessage.includes('already exists')) {
        setError(t('An invoice already exists for this maintenance. Cancel the existing invoice first.'));
      } else if (errorMessage.includes('Customer data contains values that are too long')) {
        setError(t('Customer data is too long for invoice. Please fix the customer phone number and business ID.'));
      } else {
        console.error('Quick payment: failed to create invoice', err);
        setError(t('Failed to create invoice'));
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth data-testid="quick-pay-dialog">
      <DialogTitle>{method === 'card' ? t('Card payment') : t('Cash payment')}</DialogTitle>
      <DialogContent>
        {!payer ? (
          <Alert severity="warning">
            {t('No customer assigned to this maintenance. Please assign a customer first.')}
          </Alert>
        ) : (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              {method === 'card' ? t('quickPay.cardBody') : t('quickPay.cashBody')}
            </DialogContentText>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
              <Typography color="text.secondary">{t('quickPay.payer')}</Typography>
              <Typography sx={{ fontWeight: 500, textAlign: 'right', wordBreak: 'break-word' }} data-testid="quick-pay-payer">
                {payer.name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Typography color="text.secondary">{t('Total')}</Typography>
              <Typography sx={{ fontWeight: 700 }} data-testid="quick-pay-total">
                {formatPriceWithCurrency(total)}
              </Typography>
            </Box>
          </>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={creating}>{t('Cancel')}</Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleConfirm}
          disabled={creating || !payer}
          startIcon={creating ? <CircularProgress size={16} /> : null}
          data-testid="quick-pay-confirm"
        >
          {t('Confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(QuickPayConfirmDialog);
